import CardDetailLayout from "../components/CardDetailLayout";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import PublishButton from "../components/PublishButton";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useBoardPermissions } from "../hooks/useBoardPermissions";
import { IntegrationAlert, OPENAI_KEY_REQUIRED_TITLE, useOpenAIKeyModal } from "@/integrations";

const AIPromptDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const openAiKeyModal = useOpenAIKeyModal();

  const inspectionType = subcategory.inspectionType;
  const advisorType = subcategory.advisorType;
  const promoteType = subcategory.promoteType;
  const cardType = subcategory.cardType;
  const fieldAdvisorCardType = subcategory.fieldAdvisorCardType;

  if (!inspectionType && !advisorType && !promoteType) {
    console.error("AIPromptDetail requires inspectionType, advisorType, or promoteType", subcategory);
    return (
      <div className="p-6">
        <p className="text-destructive">Configuration error: Missing prompt type information</p>
      </div>
    );
  }

  if (inspectionType && !cardType) {
    console.error("AIPromptDetail with inspectionType requires cardType", subcategory);
    return (
      <div className="p-6">
        <p className="text-destructive">Configuration error: Missing card type information</p>
      </div>
    );
  }

  if (advisorType && !fieldAdvisorCardType) {
    console.error("AIPromptDetail with advisorType requires fieldAdvisorCardType", subcategory);
    return (
      <div className="p-6">
        <p className="text-destructive">Configuration error: Missing field advisor card type information</p>
      </div>
    );
  }

  // Subscribe to board preferences changes to trigger rerender if open api key flag changes
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      return record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey;
    }
  });

  // @ts-expect-error - userPreferences is dynamically set in bryntumContext
  const hasOpenAIKey = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";

  // Use shared permission hook
  const { canManageSecrets } = useBoardPermissions();

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Publish Button Section */}
      {hasOpenAIKey && (
        <PublishButton inspectionType={inspectionType} advisorType={advisorType} promoteType={promoteType} />
      )}

      {/* OpenAI Key Warning */}
      {!hasOpenAIKey && (
        <IntegrationAlert
          className="mx-6 mt-6 mb-0 flex-shrink-0"
          variant="warning"
          provider="openai"
          title={OPENAI_KEY_REQUIRED_TITLE}
          message={
            canManageSecrets
              ? "To configure AI Prompt settings, you need to set up your OpenAI API key first. Click the button to configure your API key."
              : "To configure AI Prompt settings, you need to set up your OpenAI API key first. The Board Owner or a Board Admin can configure the API key."
          }
          action={
            canManageSecrets
              ? { label: "Configure Key", onClick: () => openAiKeyModal?.open() }
              : undefined
          }
        />
      )}

      {/* Content Area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <CardDetailLayout subcategory={subcategory} disableEditing={!hasOpenAIKey} />
      </div>
    </div>
  );
};

export default AIPromptDetail;
